import React from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { Home } from 'lucide-react';

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbNavigationProps {
  items: BreadcrumbItem[];
  currentPage: string;
  className?: string;
}

/**
 * BreadcrumbNavigation - Visible breadcrumb navigation for user experience and SEO
 * 
 * This component provides actual visible breadcrumb navigation that users can see and interact with.
 * It complements the schema-only breadcrumbs by providing real navigation benefits.
 * 
 * Features:
 * - Responsive design that works on mobile and desktop
 * - Proper aria-labels for accessibility
 * - SEO-friendly structure with proper links
 * - Consistent styling with Colorado First Plumbing brand
 */
export const BreadcrumbNavigation: React.FC<BreadcrumbNavigationProps> = ({
  items,
  currentPage,
  className = ''
}) => {
  // Convert internal URLs to proper React Router paths
  const convertUrl = (url: string): string => {
    if (url === 'https://colorado-plumbing.com/') return '/';
    return url.replace('https://colorado-plumbing.com', '').replace('#', '/#');
  };

  return (
    <div className={`w-full bg-gray-50 border-b border-gray-200 ${className}`}>
      <div className="container-custom py-3">
        <Breadcrumb>
          <BreadcrumbList className="flex-wrap">
            {/* Home icon link */}
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link 
                  to="/" 
                  className="flex items-center gap-1 text-gray-600 hover:text-trust-primary transition-colors"
                  aria-label="Go to homepage"
                >
                  <Home className="h-4 w-4" />
                  <span className="sr-only sm:not-sr-only">Home</span>
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>

            {/* Dynamic breadcrumb items */}
            {items.map((item, index) => (
              <React.Fragment key={index}>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link 
                      to={convertUrl(item.url)}
                      className="text-gray-600 hover:text-trust-primary transition-colors max-w-[150px] sm:max-w-none truncate"
                      title={item.name}
                    >
                      {item.name}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </React.Fragment>
            ))}

            {/* Current page */}
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="text-trust-primary font-medium max-w-[150px] sm:max-w-none truncate">
                {currentPage}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
    </div>
  );
};

// Helper function to create breadcrumb data for common page types
export const createBreadcrumbData = {
  // Location pages
  location: (city: string): { items: BreadcrumbItem[]; currentPage: string } => ({
    items: [
      { name: 'Service Areas', url: 'https://colorado-plumbing.com/#service-area' }
    ],
    currentPage: `${city} Plumber`
  }),

  // Service pages
  service: (serviceName: string): { items: BreadcrumbItem[]; currentPage: string } => ({
    items: [
      { name: 'Services', url: 'https://colorado-plumbing.com/#services' }
    ],
    currentPage: serviceName
  }),

  // Pricing pages
  pricing: (serviceName: string): { items: BreadcrumbItem[]; currentPage: string } => ({
    items: [
      { name: 'Pricing', url: 'https://colorado-plumbing.com/pricing' }
    ],
    currentPage: `${serviceName} Pricing`
  }),

  // Team pages
  team: (memberName?: string): { items: BreadcrumbItem[]; currentPage: string } => ({
    items: [
      { name: 'About', url: 'https://colorado-plumbing.com/#about' },
      ...(memberName ? [{ name: 'Meet the Team', url: 'https://colorado-plumbing.com/meet-the-team' }] : [])
    ],
    currentPage: memberName || 'Meet the Team'
  }),

  // Utility pages
  utility: (pageName: string): { items: BreadcrumbItem[]; currentPage: string } => ({
    items: [],
    currentPage: pageName
  })
};

export default BreadcrumbNavigation;